/**
 * MiniGameBase - Minimal base class for mini-game engines
 *
 * This provides only:
 * - State management (fen, moves, targets, status)
 * - Config access
 * - Abstract methods for game-specific logic
 *
 * It does NOT handle:
 * - Chess move calculation (use BoardUtils directly)
 * - Turn management (each game decides)
 * - Specific win/fail conditions (override in subclass)
 */
import * as BoardUtils from "./BoardUtils";
export class MiniGameBase {
    config;
    state;
    pieces;
    constructor(config) {
        this.config = config;
        this.pieces = [];
        this.state = this.initializeState();
    }
    /**
     * Initialize game state. Override for custom initialization.
     */
    initializeState() {
        // Parse FEN to get initial pieces
        const board = BoardUtils.parseFenToBoard(this.config.fen);
        if (board) {
            this.pieces = board.pieces;
        }
        return {
            fen: this.config.fen,
            moves: [],
            moveCount: 0,
            targetsAchieved: 0,
            totalTargets: this.config.targets?.length || 0,
            remainingTargets: [...(this.config.targets || [])],
            foundSquares: [],
            wrongSquares: [],
            status: "playing",
        };
    }
    // ========================================
    // Public Interface (MiniGameEngineInterface)
    // ========================================
    getConfig() {
        return { ...this.config };
    }
    getState() {
        return {
            ...this.state,
            moves: [...this.state.moves],
            remainingTargets: [...this.state.remainingTargets],
            foundSquares: [...this.state.foundSquares],
            wrongSquares: [...this.state.wrongSquares],
        };
    }
    getPieces() {
        return [...this.pieces];
    }
    getPieceAt(square) {
        return BoardUtils.getPieceAt(this.pieces, square);
    }
    reset() {
        this.state = this.initializeState();
    }
    /**
     * Handle a text answer for input-based games.
     * Non-input games return an error by default — override in input-based engines.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    handleInput(_text) {
        return { valid: false, error: "This game does not support text input" };
    }
    // ========================================
    // Helper methods for subclasses
    // ========================================
    /**
     * Check if a target was hit and update state.
     */
    checkTargetHit(square) {
        if (this.state.remainingTargets.includes(square)) {
            this.state.targetsAchieved++;
            this.state.remainingTargets = this.state.remainingTargets.filter((t) => t !== square);
            return true;
        }
        return false;
    }
    /**
     * Check move limit exceeded.
     */
    isMoveLimitExceeded() {
        return (this.config.moveLimit !== undefined &&
            this.state.moveCount >= this.config.moveLimit);
    }
    /**
     * Update FEN from current pieces.
     */
    updateFen() {
        this.state.fen = BoardUtils.piecesToFen(this.pieces, this.config.playerColor);
    }
}
